import { useEffect, useMemo, useRef, useState } from 'react';
import { navigate, useCtx } from '../lib.ts';
import { Icon, type IconName } from './Icon.tsx';

interface Item {
  label: string;
  hint: string;
  kind: string;
  icon: IconName;
  to: string;
}

/** ⌘K: jump to any page, round, camera or player of the user's scoped snapshot. */
export function CommandPalette({ nav, onClose }: { nav: { path: string; label: string; icon: IconName; group: string }[]; onClose: () => void }) {
  const { data } = useCtx();
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => input.current?.focus(), []);
  const all = useMemo<Item[]>(
    () => [
      ...nav.map((n) => ({ label: n.label, hint: n.group, kind: 'page', icon: n.icon, to: n.path })),
      ...data.rounds
        .slice()
        .sort((a, b) => (a.gameRoundId < b.gameRoundId ? 1 : -1))
        .slice(0, 300)
        .map((r) => ({ label: r.gameRoundId as string, hint: `${r.gameId} · ${r.state}${r.finalCount !== null ? ` · count ${r.finalCount}` : ''}`, kind: 'round', icon: 'rounds' as IconName, to: `/rounds/${encodeURIComponent(r.gameRoundId)}` })),
      ...data.cameras.map((c) => ({ label: c.cameraAlias as string, hint: `${c.displayName} · ${c.status}`, kind: 'camera', icon: 'camera' as IconName, to: `/cameras/${encodeURIComponent(c.cameraAlias)}` })),
      ...data.players.slice(0, 300).map((p) => ({ label: p.playerRef, hint: `${p.operatorId} · ${p.brandId}`, kind: 'player', icon: 'players' as IconName, to: `/players/${encodeURIComponent(p.playerRef)}` })),
    ],
    [nav, data],
  );
  const hits = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return all.filter((i) => i.kind === 'page');
    return all.filter((i) => `${i.label} ${i.hint} ${i.kind}`.toLowerCase().includes(t)).slice(0, 40);
  }, [q, all]);
  const go = (i: Item | undefined) => {
    if (!i) return;
    onClose();
    navigate(i.to);
  };
  return (
    <div className="palette-backdrop" onClick={onClose} role="presentation">
      <div className="palette" role="dialog" aria-modal="true" aria-label="Search" onClick={(e) => e.stopPropagation()} data-testid="palette">
        <div className="palette-input">
          <Icon name="search" />
          <input
            ref={input}
            value={q}
            placeholder="Search pages, rounds, cameras, players…"
            aria-label="Search"
            aria-controls="palette-list"
            onChange={(e) => (setQ(e.target.value), setSel(0))}
            onKeyDown={(e) => {
              if (e.key === 'Escape') onClose();
              else if (e.key === 'Enter') go(hits[sel]);
              else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                const d = e.key === 'ArrowDown' ? 1 : -1;
                setSel((s) => Math.min(hits.length - 1, Math.max(0, s + d)));
              }
            }}
          />
        </div>
        {hits.length === 0 ? (
          <p className="palette-empty">Nothing matches “{q}”.</p>
        ) : (
          <ul className="palette-list" id="palette-list" role="listbox">
            {hits.map((i, n) => (
              <li key={`${i.kind}-${i.to}`} role="option" aria-selected={n === sel} onMouseEnter={() => setSel(n)} onClick={() => go(i)}>
                <Icon name={i.icon} size={16} />
                <span>
                  {i.label} <span className="muted">· {i.hint}</span>
                </span>
                <span className="kind">{i.kind}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="palette-foot">
          <span>↑↓ move</span>
          <span>↵ open</span>
          <span>esc close</span>
        </div>
      </div>
    </div>
  );
}
